import React, { createContext, useContext, useReducer, useEffect } from "react";
import mockData from "../data/mockData";

// Types
export interface Member {
  id: string;
  name: string;
  email: string;
  phone: string;
  licenseNumber: string;
  licenseStatus: "active" | "expired" | "suspended";
  membershipType: "pilot" | "retired" | "honorary";
  joinDate: string;
  renewalDate: string;
  qualifications: string[];
  profileImage?: string;
  address?: string;
  emergencyContact?: string;
  digitalIdCard?: string;
  subscriptionStatus: "active" | "expired" | "pending";
  lastPaymentDate?: string;
  unionHistory: UnionHistoryEntry[];
}

export interface UnionHistoryEntry {
  id: string;
  date: string;
  action: string;
  description: string;
  committee?: string;
}

export interface Meeting {
  id: string;
  title: string;
  date: string;
  type: "board" | "committee" | "general";
  status: "scheduled" | "in-progress" | "completed";
  agenda: string[];
  attendees: string[];
  minutes?: string;
  resolutions?: string[];
  votingItems?: VotingItem[];
  location?: string;
  duration?: number;
  committee?: string;
}

export interface VotingItem {
  id: string;
  title: string;
  description: string;
  options: string[];
  votes: { [option: string]: string[] }; // option -> voter IDs
  status: "pending" | "active" | "completed";
  deadline?: string;
}

export interface Case {
  id: string;
  title: string;
  type: "dispute" | "labor" | "regulatory";
  status: "open" | "in-progress" | "resolved" | "closed";
  assignedTo: string;
  priority: "low" | "medium" | "high";
  createdDate: string;
  dueDate?: string;
  description: string;
  documents: string[];
  caseNumber: string;
  parties: string[];
  legalAdvisor?: string;
  hearingDate?: string;
  outcome?: string;
  relatedCases?: string[];
}

export interface FinancialRecord {
  id: string;
  type: "income" | "expense";
  category: string;
  amount: number;
  date: string;
  description: string;
  reference?: string;
  department?: string;
  budgetLine?: string;
  paymentMethod?: string;
  invoiceNumber?: string;
  approvedBy?: string;
}

export interface Event {
  id: string;
  title: string;
  date: string;
  type:
    | "conference"
    | "training"
    | "orientation"
    | "social"
    | "cultural"
    | "professional"
    | "ceremony";
  location: string;
  attendees: number;
  budget: number;
  status: "planned" | "confirmed" | "completed" | "cancelled";
  organizer?: string;
  description?: string;
  maxAttendees?: number;
  registrationDeadline?: string;
  committee?: string;
  sponsors?: string[];
}

// New interfaces for expanded modules
export interface Committee {
  id: string;
  name: string;
  type: "international" | "dispute" | "social" | "media" | "artistic" | "other";
  description: string;
  members: string[];
  chairperson: string;
  establishedDate: string;
  status: "active" | "inactive";
  budget?: number;
  objectives: string[];
}

export interface InternationalPartner {
  id: string;
  name: string;
  type: "ICAO" | "airline" | "foreign_syndicate" | "government" | "other";
  country: string;
  contactPerson: string;
  email: string;
  phone: string;
  relationship: string;
  lastContact: string;
  agreements: string[];
  status: "active" | "inactive";
}

export interface DisputeCase {
  id: string;
  caseNumber: string;
  title: string;
  complainant: string;
  respondent: string;
  type: "employment" | "contract" | "disciplinary" | "other";
  status: "submitted" | "under_review" | "mediation" | "resolved" | "closed";
  priority: "low" | "medium" | "high";
  assignedMediator: string;
  createdDate: string;
  dueDate?: string;
  description: string;
  documents: string[];
  mediationSessions: MediationSession[];
  outcome?: string;
  resolution?: string;
}

export interface MediationSession {
  id: string;
  date: string;
  attendees: string[];
  notes: string;
  outcome?: string;
  nextSteps?: string;
}

export interface MediaContent {
  id: string;
  title: string;
  type:
    | "press_release"
    | "newsletter"
    | "announcement"
    | "social_media"
    | "article";
  content: string;
  status: "draft" | "review" | "approved" | "published";
  author: string;
  createdDate: string;
  publishDate?: string;
  targetAudience: string[];
  channels: string[];
  approvalWorkflow: ApprovalStep[];
  mediaMentions?: MediaMention[];
}

export interface ApprovalStep {
  id: string;
  approver: string;
  status: "pending" | "approved" | "rejected";
  comments?: string;
  date?: string;
}

export interface MediaMention {
  id: string;
  source: string;
  url: string;
  date: string;
  sentiment: "positive" | "neutral" | "negative";
  summary: string;
}

export interface Asset {
  id: string;
  name: string;
  type: "hardware" | "software" | "license" | "furniture" | "other";
  category: string;
  serialNumber?: string;
  purchaseDate: string;
  cost: number;
  status: "active" | "maintenance" | "retired" | "lost";
  assignedTo?: string;
  location: string;
  warrantyExpiry?: string;
  maintenanceSchedule?: MaintenanceRecord[];
}

export interface MaintenanceRecord {
  id: string;
  date: string;
  type: "preventive" | "repair" | "upgrade";
  description: string;
  cost: number;
  technician: string;
  nextDue?: string;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  type: "technical" | "hardware" | "software" | "network" | "other";
  priority: "low" | "medium" | "high" | "urgent";
  status: "open" | "in-progress" | "resolved" | "closed";
  reporter: string;
  assignee?: string;
  createdDate: string;
  dueDate?: string;
  resolution?: string;
  attachments: string[];
  comments: TicketComment[];
}

export interface TicketComment {
  id: string;
  author: string;
  content: string;
  date: string;
  isInternal: boolean;
}

export interface Correspondence {
  id: string;
  title: string;
  type: "incoming" | "outgoing";
  reference: string;
  date: string;
  from: string;
  to: string;
  subject: string;
  content: string;
  priority: "low" | "medium" | "high";
  status: "draft" | "sent" | "received" | "archived";
  attachments: string[];
  relatedCases?: string[];
  assignedTo?: string;
  dueDate?: string;
}

export interface Budget {
  id: string;
  year: number;
  department: string;
  category: string;
  allocated: number;
  spent: number;
  remaining: number;
  status: "draft" | "approved" | "active" | "closed";
  approvedBy?: string;
  approvedDate?: string;
  notes?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: "admin" | "board" | "member" | "committee" | "secretariat";
  permissions: string[];
  lastLogin?: string;
  isActive: boolean;
  department?: string;
  committee?: string;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "warning" | "error" | "success";
  recipient: string;
  isRead: boolean;
  createdDate: string;
  actionUrl?: string;
  priority: "low" | "medium" | "high";
}

// State interface
interface AppState {
  members: Member[];
  meetings: Meeting[];
  cases: Case[];
  financialRecords: FinancialRecord[];
  events: Event[];
  committees: Committee[];
  internationalPartners: InternationalPartner[];
  disputeCases: DisputeCase[];
  mediaContent: MediaContent[];
  assets: Asset[];
  tickets: Ticket[];
  correspondence: Correspondence[];
  budgets: Budget[];
  users: User[];
  notifications: Notification[];
  currentUser: User;
}

// Action types
type AppAction =
  | { type: "SET_MEMBERS"; payload: Member[] }
  | { type: "ADD_MEMBER"; payload: Member }
  | { type: "UPDATE_MEMBER"; payload: Member }
  | { type: "DELETE_MEMBER"; payload: string }
  | { type: "SET_MEETINGS"; payload: Meeting[] }
  | { type: "ADD_MEETING"; payload: Meeting }
  | { type: "UPDATE_MEETING"; payload: Meeting }
  | { type: "SET_CASES"; payload: Case[] }
  | { type: "ADD_CASE"; payload: Case }
  | { type: "UPDATE_CASE"; payload: Case }
  | { type: "SET_FINANCIAL_RECORDS"; payload: FinancialRecord[] }
  | { type: "ADD_FINANCIAL_RECORD"; payload: FinancialRecord }
  | { type: "SET_EVENTS"; payload: Event[] }
  | { type: "ADD_EVENT"; payload: Event }
  | { type: "UPDATE_EVENT"; payload: Event }
  | { type: "SET_COMMITTEES"; payload: Committee[] }
  | { type: "ADD_COMMITTEE"; payload: Committee }
  | { type: "UPDATE_COMMITTEE"; payload: Committee }
  | { type: "SET_INTERNATIONAL_PARTNERS"; payload: InternationalPartner[] }
  | { type: "ADD_INTERNATIONAL_PARTNER"; payload: InternationalPartner }
  | { type: "UPDATE_INTERNATIONAL_PARTNER"; payload: InternationalPartner }
  | { type: "SET_DISPUTE_CASES"; payload: DisputeCase[] }
  | { type: "ADD_DISPUTE_CASE"; payload: DisputeCase }
  | { type: "UPDATE_DISPUTE_CASE"; payload: DisputeCase }
  | { type: "SET_MEDIA_CONTENT"; payload: MediaContent[] }
  | { type: "ADD_MEDIA_CONTENT"; payload: MediaContent }
  | { type: "UPDATE_MEDIA_CONTENT"; payload: MediaContent }
  | { type: "SET_ASSETS"; payload: Asset[] }
  | { type: "ADD_ASSET"; payload: Asset }
  | { type: "UPDATE_ASSET"; payload: Asset }
  | { type: "SET_TICKETS"; payload: Ticket[] }
  | { type: "ADD_TICKET"; payload: Ticket }
  | { type: "UPDATE_TICKET"; payload: Ticket }
  | { type: "SET_CORRESPONDENCE"; payload: Correspondence[] }
  | { type: "ADD_CORRESPONDENCE"; payload: Correspondence }
  | { type: "UPDATE_CORRESPONDENCE"; payload: Correspondence }
  | { type: "SET_BUDGETS"; payload: Budget[] }
  | { type: "ADD_BUDGET"; payload: Budget }
  | { type: "UPDATE_BUDGET"; payload: Budget }
  | { type: "SET_USERS"; payload: User[] }
  | { type: "ADD_USER"; payload: User }
  | { type: "UPDATE_USER"; payload: User }
  | { type: "SET_NOTIFICATIONS"; payload: Notification[] }
  | { type: "ADD_NOTIFICATION"; payload: Notification }
  | { type: "UPDATE_NOTIFICATION"; payload: Notification }
  | { type: "LOAD_DATA"; payload: Partial<AppState> };

// Initial state
const initialState: AppState = {
  members: [],
  meetings: [],
  cases: [],
  financialRecords: [],
  events: [],
  committees: [],
  internationalPartners: [],
  disputeCases: [],
  mediaContent: [],
  assets: [],
  tickets: [],
  correspondence: [],
  budgets: [],
  users: [],
  notifications: [],
  currentUser: {
    id: "1",
    name: "Admin User",
    email: "admin@syndicate.eg",
    role: "admin",
    permissions: ["all"],
    isActive: true,
    department: "Administration",
  },
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "SET_MEMBERS":
      return { ...state, members: action.payload };
    case "ADD_MEMBER":
      return { ...state, members: [...state.members, action.payload] };
    case "UPDATE_MEMBER":
      return {
        ...state,
        members: state.members.map((m) =>
          m.id === action.payload.id ? action.payload : m
        ),
      };
    case "DELETE_MEMBER":
      return {
        ...state,
        members: state.members.filter((m) => m.id !== action.payload),
      };
    case "SET_MEETINGS":
      return { ...state, meetings: action.payload };
    case "ADD_MEETING":
      return { ...state, meetings: [...state.meetings, action.payload] };
    case "UPDATE_MEETING":
      return {
        ...state,
        meetings: state.meetings.map((m) =>
          m.id === action.payload.id ? action.payload : m
        ),
      };
    case "SET_CASES":
      return { ...state, cases: action.payload };
    case "ADD_CASE":
      return { ...state, cases: [...state.cases, action.payload] };
    case "UPDATE_CASE":
      return {
        ...state,
        cases: state.cases.map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
      };
    case "SET_FINANCIAL_RECORDS":
      return { ...state, financialRecords: action.payload };
    case "ADD_FINANCIAL_RECORD":
      return {
        ...state,
        financialRecords: [...state.financialRecords, action.payload],
      };
    case "SET_EVENTS":
      return { ...state, events: action.payload };
    case "ADD_EVENT":
      return { ...state, events: [...state.events, action.payload] };
    case "UPDATE_EVENT":
      return {
        ...state,
        events: state.events.map((e) =>
          e.id === action.payload.id ? action.payload : e
        ),
      };
    case "SET_COMMITTEES":
      return { ...state, committees: action.payload };
    case "ADD_COMMITTEE":
      return { ...state, committees: [...state.committees, action.payload] };
    case "UPDATE_COMMITTEE":
      return {
        ...state,
        committees: state.committees.map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
      };
    case "SET_INTERNATIONAL_PARTNERS":
      return { ...state, internationalPartners: action.payload };
    case "ADD_INTERNATIONAL_PARTNER":
      return {
        ...state,
        internationalPartners: [...state.internationalPartners, action.payload],
      };
    case "UPDATE_INTERNATIONAL_PARTNER":
      return {
        ...state,
        internationalPartners: state.internationalPartners.map((p) =>
          p.id === action.payload.id ? action.payload : p
        ),
      };
    case "SET_DISPUTE_CASES":
      return { ...state, disputeCases: action.payload };
    case "ADD_DISPUTE_CASE":
      return {
        ...state,
        disputeCases: [...state.disputeCases, action.payload],
      };
    case "UPDATE_DISPUTE_CASE":
      return {
        ...state,
        disputeCases: state.disputeCases.map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
      };
    case "SET_MEDIA_CONTENT":
      return { ...state, mediaContent: action.payload };
    case "ADD_MEDIA_CONTENT":
      return {
        ...state,
        mediaContent: [...state.mediaContent, action.payload],
      };
    case "UPDATE_MEDIA_CONTENT":
      return {
        ...state,
        mediaContent: state.mediaContent.map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
      };
    case "SET_ASSETS":
      return { ...state, assets: action.payload };
    case "ADD_ASSET":
      return { ...state, assets: [...state.assets, action.payload] };
    case "UPDATE_ASSET":
      return {
        ...state,
        assets: state.assets.map((a) =>
          a.id === action.payload.id ? action.payload : a
        ),
      };
    case "SET_TICKETS":
      return { ...state, tickets: action.payload };
    case "ADD_TICKET":
      return { ...state, tickets: [...state.tickets, action.payload] };
    case "UPDATE_TICKET":
      return {
        ...state,
        tickets: state.tickets.map((t) =>
          t.id === action.payload.id ? action.payload : t
        ),
      };
    case "SET_CORRESPONDENCE":
      return { ...state, correspondence: action.payload };
    case "ADD_CORRESPONDENCE":
      return {
        ...state,
        correspondence: [...state.correspondence, action.payload],
      };
    case "UPDATE_CORRESPONDENCE":
      return {
        ...state,
        correspondence: state.correspondence.map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
      };
    case "SET_BUDGETS":
      return { ...state, budgets: action.payload };
    case "ADD_BUDGET":
      return { ...state, budgets: [...state.budgets, action.payload] };
    case "UPDATE_BUDGET":
      return {
        ...state,
        budgets: state.budgets.map((b) =>
          b.id === action.payload.id ? action.payload : b
        ),
      };
    case "SET_USERS":
      return { ...state, users: action.payload };
    case "ADD_USER":
      return { ...state, users: [...state.users, action.payload] };
    case "UPDATE_USER":
      return {
        ...state,
        users: state.users.map((u) =>
          u.id === action.payload.id ? action.payload : u
        ),
      };
    case "SET_NOTIFICATIONS":
      return { ...state, notifications: action.payload };
    case "ADD_NOTIFICATION":
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };
    case "UPDATE_NOTIFICATION":
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.id === action.payload.id ? action.payload : n
        ),
      };
    case "LOAD_DATA":
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

// Context
const DataContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  resetToMockData: () => void;
} | null>(null);

// Provider
export function DataProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem("syndicate-erp-data");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        // Check if the saved data has any meaningful content
        const hasData =
          parsedData.members?.length > 0 ||
          parsedData.meetings?.length > 0 ||
          parsedData.cases?.length > 0;

        if (hasData) {
          dispatch({ type: "LOAD_DATA", payload: parsedData });
        } else {
          // Initialize with sample data if saved data is empty
          initializeSampleData();
        }
      } catch (error) {
        // Error loading saved data, initialize with sample data
        initializeSampleData();
      }
    } else {
      // Initialize with sample data
      initializeSampleData();
    }
  }, []);

  // Save data to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem("syndicate-erp-data", JSON.stringify(state));
  }, [state]);

  const initializeSampleData = () => {
    // Load mock data from the imported file
    dispatch({ type: "SET_MEMBERS", payload: mockData.members });
    dispatch({ type: "SET_MEETINGS", payload: mockData.meetings });
    dispatch({ type: "SET_CASES", payload: mockData.cases });
    dispatch({
      type: "SET_FINANCIAL_RECORDS",
      payload: mockData.financialRecords,
    });
    dispatch({ type: "SET_EVENTS", payload: mockData.events });
    dispatch({ type: "SET_COMMITTEES", payload: mockData.committees });
    dispatch({ type: "SET_ASSETS", payload: mockData.assets });
    dispatch({ type: "SET_TICKETS", payload: mockData.tickets });
    dispatch({ type: "SET_CORRESPONDENCE", payload: mockData.correspondence });
  };

  const resetToMockData = () => {
    localStorage.removeItem("syndicate-erp-data");
    initializeSampleData();
  };

  return (
    <DataContext.Provider value={{ state, dispatch, resetToMockData }}>
      {children}
    </DataContext.Provider>
  );
}

// Hook
export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
}
